// Compact line labels for the "release line vs market line" display (#1848). The
// market line is either the CLOSING line (graded picks: closingSide/closingTotal) or
// the CURRENT line (pending picks: currentLine.number), both stored from OUR pick's
// perspective — same convention as the release `side`/`total`. Mirrors the existing
// closing-line cell in ResultsTable so the two read consistently.

type LineRelease = Pick<
  API.Release,
  'playType' | 'sideIndicator' | 'side' | 'totalIndicator' | 'total' | 'visitor' | 'home'
>;

/** A spread number with RAS's sign convention: 0 → "PK", positive → "+3.5". */
export function formatSpread(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (value === 0) {
    return 'PK';
  }
  if (value > 0) {
    return `+${Number(value)}`;
  }
  return `${Number(value)}`;
}

/** A signed delta (points of edge vs the market), e.g. "+0.5" / "-1". "0" → "+0.0". */
export function formatDelta(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (value === 0) {
    return '+0.0';
  }
  if (value > 0) {
    return `+${Number(value)}`;
  }
  return `${Number(value)}`;
}

const teamShort = (t?: API.Team) => t?.shortName || t?.name || '';

/** The picked team's short name for a side play (from our side indicator). */
export function pickedTeamShort(release: LineRelease): string {
  if (release.sideIndicator === 'visitor') {
    return teamShort(release.visitor);
  }
  if (release.sideIndicator === 'home') {
    return teamShort(release.home);
  }
  return '';
}

/** The O/U letter for a total play ("O" / "U"), or "" for a side. */
export function totalLetter(release: LineRelease): string {
  if (!release.totalIndicator) {
    return '';
  }
  return release.totalIndicator[0].toUpperCase();
}

/**
 * The release line as a compact label: side → "KC -3", total → "O 44.5".
 * Returns "" when the play isn't a side/total with a number.
 */
export function releaseLineLabel(release: LineRelease): string {
  if (release.playType === 'side' && release.side != null) {
    const team = pickedTeamShort(release);
    return `${team} ${formatSpread(release.side)}`.trim();
  }
  if (release.playType === 'total' && release.total != null) {
    return `${totalLetter(release)} ${Number(release.total)}`.trim();
  }
  return '';
}

/**
 * The market line number ALONE (no team), for the muted meta line in the redesigned
 * results rows (#2004) where the picked team already leads the row title. Side → the
 * signed spread ("-3.5" / "PK" / "+3"); total → a lowercase O/U + number ("o46").
 * `number` is from our pick's perspective. Returns "" when there's nothing to show.
 */
export function compactLineLabel(
  release: Pick<LineRelease, 'playType' | 'totalIndicator'>,
  number: number | null | undefined,
): string {
  if (number === null || number === undefined) {
    return '';
  }
  if (release.playType === 'side') {
    return formatSpread(number);
  }
  if (release.playType === 'total') {
    return `${(release.totalIndicator?.[0] || 'o').toLowerCase()}${Number(number)}`;
  }
  return '';
}

/**
 * The market line (closing OR current) as a compact label matching the release label's
 * shape: side → "KC -3.5", total → "O 44". `marketNumber` is from our pick's
 * perspective. Returns "" when there's nothing to show.
 */
export function marketLineLabel(
  release: LineRelease,
  marketNumber: number | null | undefined,
): string {
  if (marketNumber === null || marketNumber === undefined) {
    return '';
  }
  if (release.playType === 'side') {
    const team = pickedTeamShort(release);
    return `${team} ${formatSpread(marketNumber)}`.trim();
  }
  if (release.playType === 'total') {
    return `${totalLetter(release)} ${Number(marketNumber)}`.trim();
  }
  return '';
}

/**
 * Resolve the market line to display for a release: the CLOSING line for a graded pick,
 * or the CURRENT line for a pending pick (when captured). Returns null when there's no
 * market line to show yet.
 */
export function resolveMarketLine(release: API.Release): {
  number: number;
  value: number | null;
  kind: 'closing' | 'current';
  capturedAt?: string | null;
  provider?: string | null;
} | null {
  const graded = release.result !== 'pending';
  if (graded) {
    const number =
      release.playType === 'side'
        ? release.closingSide
        : release.playType === 'total'
          ? release.closingTotal
          : null;
    if (number === null || number === undefined) {
      return null;
    }
    return { number, value: release.closingLineValue ?? null, kind: 'closing' };
  }
  const current = release.currentLine;
  if (!current || current.number === null || current.number === undefined) {
    return null;
  }
  return {
    number: current.number,
    value: current.value ?? null,
    kind: 'current',
    capturedAt: current.capturedAt,
    provider: current.provider,
  };
}
